import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ConfirmSheet } from './ConfirmSheet'
import { supabase } from '../lib/supabase'
import { labsWrite, isLabsMode } from '../lib/labs'
import { useMentions } from '../hooks/useMentions'
import { reportSaveError } from '../lib/saveError'
import { getValidSeasons } from '../lib/episode'
import { extractMentions } from '../lib/reviewMarkup'
import { postFn } from '../lib/postFn'
import { ReelRating } from './ReelRating'
import { ReviewBody } from './ReviewBody'
import { Avatar } from './Avatar'
import { X, Save, Trash2, Lock, Globe, AlertTriangle, Bold, Italic, EyeOff, Eye, AtSign, Feather, ChevronRight } from 'lucide-react'

// ── ReviewComposer ────────────────────────────────────────────────────────────
// Near-full-screen writing surface for a show/season review — the "give it room"
// upgrade over the old 5-row inline box. Pins the reel RATING at the top (rating and
// review are one act), supports markdown-lite (**bold** *italic* ||spoiler||) via a
// 3-button bar (no toolbar) with a live Preview, and keeps the existing private→public
// + spoiler-flag model. Owns all persistence to the `reviews` table; the calling
// ReviewCard just displays the result and reopens this to edit.

export function ReviewComposer({ user, show, profile, rating = 0, onRate, initialTarget = null, isNew = false, milestone = null, forceNew = false, onClose, onSaved }) {
  const navigate = useNavigate()
  // Staged like the Writers' Room: a NEW review opens on an "intent" beat (set the
  // stage — who sees it, spoilers?), then the compose surface, then a short "published"
  // payoff. Editing an existing review skips straight to compose.
  const [phase, setPhase]     = useState(isNew ? 'intent' : 'compose')
  const [id, setId]           = useState(null)
  const [body, setBody]       = useState('')
  // Reviews default to PUBLIC so they feed the community (Reading Room, In Their Words) —
  // the writing is the whole point of the social layer. An existing review's saved value
  // overrides this on load; you can still flip any single review to private (Jason 2026-07-10).
  const [isPublic, setPublic] = useState(true)
  const [spoilers, setSpoilers] = useState(false)
  const [busy, setBusy]       = useState(false)
  const [preview, setPreview] = useState(false)
  const [target, setTarget]   = useState(initialTarget)
  const taRef = useRef(null)
  // Body as last loaded/saved — so we only notify @mentions that are NEWLY added,
  // not every existing one on each edit.
  const notifiedBodyRef = useRef('')
  const seasons = getValidSeasons(show?.seasons)

  // Load the review for the selected target (whole series or a season). Re-runs when
  // the season target changes so switching tabs shows that target's existing words.
  useEffect(() => {
    let alive = true
    if (!user?.id || !show?.id) return
    setId(null); setBody(''); setPublic(true); setSpoilers(false); notifiedBodyRef.current = ''
    // forceNew = a REVISIT ("leave a final word"): never load or overwrite an earlier take —
    // always start blank and insert a fresh row, so the original stays as the "Then".
    if (forceNew) return
    let q = supabase.from('reviews').select('*')
      .eq('user_id', user.id).eq('show_id', show.id).is('episode', null)
    q = target == null ? q.is('season', null) : q.eq('season', target)
    q.maybeSingle().then(({ data }) => {
      if (!alive || !data) return
      setId(data.id); setBody(data.body || ''); setPublic(!!data.is_public); setSpoilers(!!data.contains_spoilers)
      notifiedBodyRef.current = data.body || ''
    })
    return () => { alive = false }
  }, [user?.id, show?.id, target])

  // Lock body scroll while the full-screen composer is open.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  const syncFeed = async (nowPublic) => {
    if (target != null) return // only whole-series reviews drive the feed
    const canBroadcast = !!profile?.notification_prefs?.share_activity
    try {
      await labsWrite('remove review activity', () => supabase.from('activities').delete().eq('user_id', user.id).eq('type', 'reviewed').eq('show_id', show.id))
      if (nowPublic && canBroadcast) {
        await labsWrite('post review activity', () => supabase.from('activities').insert({
          user_id: user.id, type: 'reviewed', show_id: show.id, show_name: show.name,
          show_poster: show.poster_path || null, data: { public: true }, created_at: new Date().toISOString(),
        }))
      }
    } catch (_) { /* feed mirror is best-effort */ }
  }

  // Returns a status so the caller decides what to do next (advance to the "published"
  // beat for a new review, or just close for an edit). Never closes itself.
  const save = async () => {
    const text = body.trim()
    if (!text) return 'empty'
    setBusy(true)
    const row = {
      user_id: user.id, show_id: show.id, show_name: show.name, show_poster: show.poster_path,
      season: target, episode: null, body: text, is_public: isPublic, contains_spoilers: spoilers,
      // Snapshot the reel rating WITH the words — a rating lives once per show and gets
      // overwritten, so this frozen copy is what powers the ★★★★★ → ★★ trajectory in the
      // Then & Now display. `milestone` tags how this take was captured (finale/season/null).
      rating: rating || null,
      milestone,
      updated_at: new Date().toISOString(),
    }
    let savedId = id
    if (id) {
      const { error } = await labsWrite('update review', () => supabase.from('reviews').update(row).eq('id', id))
      if (reportSaveError(error, 'save review')) { setBusy(false); return 'error' }
    } else {
      const { data, error } = await labsWrite('insert review', () => supabase.from('reviews').insert(row).select('id').single())
      if (reportSaveError(error, 'save review')) { setBusy(false); return 'error' }
      // Labs mode never hits the DB, so there's no real id to come back — a fake one
      // lets the compose→published UI flow behave normally without a real row to edit.
      savedId = data?.id || (isLabsMode() ? `labs-${Date.now()}` : null)
      setId(savedId)
    }
    setBusy(false)
    await syncFeed(isPublic)
    // Notify @mentioned people — but only for a PUBLIC review (they can't read a private
    // one) and only usernames NEWLY added since this open (no re-pinging on every edit).
    // The server gates each recipient to someone the author follows, so this can't spam.
    if (isPublic) {
      const prev = new Set(extractMentions(notifiedBodyRef.current))
      const fresh = extractMentions(text).filter(u => !prev.has(u))
      if (fresh.length) {
        postFn('/.netlify/functions/notify-activity', {
          type: 'review_mention',
          show: { id: show.id, name: show.name, poster_path: show.poster_path },
          extra: { usernames: fresh.slice(0, 10) },
        }).catch(() => {})
      }
    }
    notifiedBodyRef.current = text
    onSaved?.({ id: savedId, body: text, is_public: isPublic, contains_spoilers: spoilers, season: target })
    return 'saved'
  }

  // Header Save: a new review advances to the "published" beat; an edit just closes.
  const onSaveTap = async () => {
    const s = await save()
    if (s === 'saved') { isNew ? setPhase('done') : onClose?.() }
    else if (s === 'empty') onClose?.()
  }

  // @mentions live in useMentions so the episode-comment composer shares this exact
  // behaviour instead of reimplementing it (see the hook's header).
  const { follows, mention, suggestions, onChange: onBodyChange, onCaret: onBodyCaret,
          insertMention, insertAt } = useMentions({ userId: user?.id, value: body, setValue: setBody, textareaRef: taRef })

  // The highest-effort content a user writes here. ReviewOverlay already two-steps the
  // delete of this same row; the composer's was bare.
  const [confirmDel, setConfirmDel] = useState(false)
  const remove = async () => {
    if (id) { setBusy(true); await labsWrite('delete review', () => supabase.from('reviews').delete().eq('id', id)); await syncFeed(false); setBusy(false) }
    onSaved?.(null)
    onClose?.()
  }

  // Wrap the current selection in a markdown marker (no toolbar — three taps max).
  const wrap = (a, b = a, fallback = 'text') => {
    const ta = taRef.current; if (!ta) return
    const s = ta.selectionStart ?? body.length, e = ta.selectionEnd ?? body.length
    const sel = body.slice(s, e) || fallback
    const next = body.slice(0, s) + a + sel + b + body.slice(e)
    setBody(next)
    requestAnimationFrame(() => {
      ta.focus()
      ta.selectionStart = s + a.length
      ta.selectionEnd = s + a.length + sel.length
    })
  }

  const targetLabel = target != null ? `Season ${target}` : show?.name
  const fmtBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: 34, height: 30, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xs)', color: 'var(--text-dim)', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 11, fontWeight: 700 }
  const chip = (active) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 'var(--radius-xs)', padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', background: active ? 'rgba(var(--gold-leaf-rgb),0.14)' : 'var(--surface)', border: '1px solid ' + (active ? 'rgba(var(--gold-leaf-rgb),0.4)' : 'var(--border)'), color: active ? 'var(--gold-leaf)' : 'var(--text-dim)' })

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)', background: 'var(--bg)', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font)' }}>
      {phase === 'intent' && (
        <IntentBeat showName={show?.name} isPublic={isPublic} setPublic={setPublic}
          spoilers={spoilers} setSpoilers={setSpoilers}
          onClose={() => onClose?.()} onStart={() => setPhase('compose')} />
      )}
      {phase === 'done' && (
        <DoneBeat isPublic={isPublic} showName={show?.name}
          onView={() => { onClose?.(); navigate('/reviews') }} onClose={() => onClose?.()} />
      )}
      {phase === 'compose' && (<>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'max(14px,env(safe-area-inset-top)) 16px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <button onClick={() => onClose?.()} aria-label="Close" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-full)', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0 }}>
          <X size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 17, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Your Review</div>
          <div style={{ color: 'var(--text-dim)', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{targetLabel}</div>
        </div>
        <button onClick={onSaveTap} disabled={busy || !body.trim()} className="ui-primary" style={{ background: body.trim() ? 'var(--grad-amber)' : 'var(--surface)', border: body.trim() ? 'none' : '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: body.trim() ? 'var(--amber-ink)' : 'var(--text-dim)', fontSize: 14, fontWeight: 800, padding: '9px 18px', cursor: busy || !body.trim() ? 'default' : 'pointer', fontFamily: 'var(--font)', display: 'flex', alignItems: 'center', gap: 5, opacity: busy ? 0.6 : 1, flexShrink: 0 }}>
          <Save size={14} /> {isNew ? 'Publish' : 'Save'}
        </button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column' }}>
        {/* Rating, the review and the score are one act */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ color: 'var(--text-dim)', fontSize: 12, fontWeight: 600 }}>Your rating</span>
          <ReelRating value={rating} onChange={(v) => onRate?.(v)} size={26} />
        </div>

        {/* Season target, whole series or a specific season */}
        {seasons.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {[{ v: null, label: 'Whole series' }, ...seasons.map(s => ({ v: s.season_number, label: `S${s.season_number}` }))].map(opt => (
              <button key={opt.v == null ? 'all' : opt.v} onClick={() => setTarget(opt.v)} style={chip(target === opt.v)}>{opt.label}</button>
            ))}
          </div>
        )}

        {/* Format bar (no toolbar, three markers) + preview toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <button onClick={() => wrap('**', '**', 'bold')} title="Bold" style={fmtBtn}><Bold size={14} /></button>
          <button onClick={() => wrap('*', '*', 'italic')} title="Italic" style={fmtBtn}><Italic size={14} /></button>
          <button onClick={() => wrap('||', '||', 'spoiler')} title="Spoiler, blurs until tapped" style={{ ...fmtBtn, width: 'auto', padding: '0 10px' }}><EyeOff size={14} /> <span style={{ fontSize: 11 }}>Spoiler</span></button>
          <button onClick={insertAt} title="Mention someone you follow" style={fmtBtn}><AtSign size={14} /></button>
          <div style={{ flex: 1 }} />
          <button onClick={() => setPreview(p => !p)} style={{ ...chip(preview) }}>
            {preview ? <><Eye size={13} /> Editing</> : <><Eye size={13} /> Preview</>}
          </button>
        </div>

        {/* Editor / preview */}
        {preview ? (
          <div style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '14px', minHeight: 240 }}>
            {body.trim()
              ? <ReviewBody text={body} contains_spoilers={spoilers} style={{ fontSize: 15 }} />
              : <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>Nothing to preview yet.</span>}
          </div>
        ) : (
          <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
            <textarea
              ref={taRef}
              value={body}
              onChange={onBodyChange}
              onKeyUp={onBodyCaret}
              onClick={onBodyCaret}
              autoFocus
              placeholder={`What worked, what didn't, the moment it hooked you…\n\nTip: **bold**, *italic*, ||spoiler||, and @mention a friend all work.`}
              style={{ flex: 1, minHeight: 240, width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', fontSize: 16, padding: '14px', resize: 'none', lineHeight: 1.6, boxSizing: 'border-box', fontFamily: 'var(--font)' }}
            />
            {/* @mention autocomplete, only people you follow */}
            {mention && suggestions.length > 0 && (
              <div style={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 5, background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', overflow: 'hidden', maxHeight: 240, overflowY: 'auto' }}>
                {suggestions.map(u => {
                  const nm = u.display_name || u.username
                  return (
                    <button key={u.id} onMouseDown={(e) => { e.preventDefault(); insertMention(u) }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', background: 'none', border: 'none', borderBottom: '1px solid var(--border-soft)', cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'left' }}>
                      <Avatar name={nm} avatarUrl={u.avatar_url} size={26} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: 'var(--text-strong)', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nm}</div>
                        <div style={{ color: 'var(--text-dim)', fontSize: 11 }}>@{u.username}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
            {mention && follows.length === 0 && (
              <div style={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 5, background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', color: 'var(--text-dim)', fontSize: 12 }}>
                Follow people to @mention them in reviews.
              </div>
            )}
          </div>
        )}

        <div style={{ color: 'var(--text-dim)', fontSize: 11, marginTop: 8 }}>
          No character limit. <b style={{ color: 'var(--text-muted)' }}>**bold**</b>, <i style={{ color: 'var(--text-muted)' }}>*italic*</i>, <span style={{ color: 'var(--text-muted)' }}>||spoiler||</span>, and <span style={{ color: 'var(--gold-leaf)' }}>@mentions</span> render for readers.
        </div>
      </div>

      {/* Footer, visibility + spoiler flag + delete */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px calc(12px + env(safe-area-inset-bottom))', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
        <button onClick={() => setPublic(p => !p)} style={chip(isPublic)} aria-pressed={isPublic}>
          {isPublic ? <><Globe size={13} /> Public</> : <><Lock size={13} /> Private</>}
        </button>
        <button onClick={() => setSpoilers(s => !s)} style={chip(spoilers)} aria-pressed={spoilers}>
          <AlertTriangle size={13} /> {spoilers ? 'Has spoilers' : 'No spoilers'}
        </button>
        <div style={{ flex: 1 }} />
        {id && (
          <button onClick={() => setConfirmDel(true)} disabled={busy} aria-label="Delete review" style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, fontFamily: 'var(--font)', padding: '6px 8px' }}>
            <Trash2 size={13} /> Delete
          </button>
        )}
      </div>
      <div style={{ padding: '0 16px 10px', color: 'var(--text-dim)', fontSize: 10, textAlign: 'center' }}>
        {isPublic ? 'Shown on your profile and to people who follow you.' : 'Only you can see this.'}
      </div>
      </>)}
      <ConfirmSheet
        open={confirmDel}
        emoji="🗑️"
        title="Delete this review?"
        body="Your written review is removed for good, along with its post in the feed."
        confirmLabel="Delete"
        cancelLabel="Keep it"
        onConfirm={() => { setConfirmDel(false); remove() }}
        onCancel={() => setConfirmDel(false)}
      />
    </div>
  )
}

// ── BEAT 1 · Set the stage ──────────────────────────────────────────────────────
// The opening move of a new review: decide who sees it and whether it's spoiler-y,
// framed as a choice rather than a toggle buried in a footer. Then → the compose beat.
function IntentBeat({ showName, isPublic, setPublic, spoilers, setSpoilers, onClose, onStart }) {
  const card = (active) => ({
    display: 'flex', alignItems: 'center', gap: 13, textAlign: 'left', width: '100%',
    padding: '15px 16px', cursor: 'pointer', fontFamily: 'var(--font)',
    background: active ? 'rgba(var(--amber-rgb),0.1)' : 'var(--bg-card)',
    border: '1px solid ' + (active ? 'rgba(var(--amber-rgb),0.45)' : 'var(--border)'),
    borderRadius: 'var(--radius-md)', transition: 'background 0.15s, border-color 0.15s',
  })
  const chip = (active) => ({
    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    borderRadius: 'var(--radius-sm)', padding: '10px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'var(--font)', background: active ? 'rgba(var(--amber-rgb),0.12)' : 'var(--surface)',
    border: '1px solid ' + (active ? 'rgba(var(--amber-rgb),0.4)' : 'var(--border)'),
    color: active ? 'var(--amber)' : 'var(--text-dim)',
  })
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', '--nxa': 'rcFade 0.3s ease' }}>
      {/* marquee rail */}
      <div aria-hidden style={{ height: 4, flexShrink: 0, background: 'repeating-linear-gradient(90deg, var(--amber) 0 6px, transparent 6px 14px)', opacity: 0.85 }} />
      <button className="ui-hit" onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 'max(16px,env(safe-area-inset-top))', right: 16, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-full)', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', cursor: 'pointer' }}>
        <X size={17} />
      </button>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '20px 22px max(24px,env(safe-area-inset-bottom))', maxWidth: 480, width: '100%', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--gold-leaf)', fontWeight: 700 }}>
            <Feather size={13} /> Your Review
          </div>
          <h2 style={{ margin: '10px 0 6px', color: 'var(--text-strong)', fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 500 }}>Set the stage</h2>
          <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 13.5, lineHeight: 1.5 }}>
            How do you want to share your take{showName ? ` on ${showName}` : ''}?
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 22 }}>
          <button onClick={() => setPublic(true)} style={card(isPublic)}>
            <div style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 12, background: 'rgba(var(--amber-rgb),0.16)', border: '1px solid rgba(var(--amber-rgb),0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Globe size={20} color="var(--gold-leaf)" /></div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-strong)', fontWeight: 700, fontSize: 15 }}>Public review</div>
              <div style={{ color: 'var(--text-dim)', fontSize: 12.5, marginTop: 2 }}>Shared to the Reading Room and the people who follow you.</div>
            </div>
          </button>
          <button onClick={() => setPublic(false)} style={card(!isPublic)}>
            <div style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Lock size={19} color="var(--text-muted)" /></div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--text-strong)', fontWeight: 700, fontSize: 15 }}>Just for me</div>
              <div style={{ color: 'var(--text-dim)', fontSize: 12.5, marginTop: 2 }}>A private note only you can see.</div>
            </div>
          </button>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ color: 'var(--text-dim)', fontSize: 12, fontWeight: 600, marginBottom: 8, textAlign: 'center' }}>Any spoilers?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setSpoilers(false)} style={chip(!spoilers)}>No spoilers</button>
            <button onClick={() => setSpoilers(true)} style={chip(spoilers)}><AlertTriangle size={13} /> Has spoilers</button>
          </div>
        </div>

        <button onClick={onStart} className="ui-primary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: 'var(--grad-amber)', border: 'none', borderRadius: 'var(--radius-md)', color: 'var(--amber-ink)', fontWeight: 800, fontSize: 15.5, padding: 15, cursor: 'pointer', fontFamily: 'var(--font)' }}>
          Start writing <ChevronRight size={17} />
        </button>
      </div>
      <style>{'@keyframes rcFade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}@keyframes rcRing{0%{transform:scale(0.4);opacity:0.7}100%{transform:scale(3);opacity:0}}'}</style>
    </div>
  )
}

// ── BEAT 3 · Published ──────────────────────────────────────────────────────────
// A short payoff so finishing a review feels like something — public reviews get a
// broadcast moment with a jump to the Reading Room; private ones get a quiet save.
function DoneBeat({ isPublic, showName, onView, onClose }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', '--nxa': 'rcFade 0.3s ease' }}>
      <div aria-hidden style={{ height: 4, flexShrink: 0, background: 'repeating-linear-gradient(90deg, var(--amber) 0 6px, transparent 6px 14px)', opacity: 0.85 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '24px 26px max(26px,env(safe-area-inset-bottom))', position: 'relative', overflow: 'hidden' }}>
        {isPublic && (
          <div aria-hidden style={{ position: 'absolute', top: '32%', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
            <span style={{ position: 'absolute', width: 64, height: 64, marginLeft: -32, borderRadius: '50%', border: '1px solid rgba(var(--amber-rgb),0.4)', '--nxa': 'rcRing 2.4s ease-out infinite' }} />
            <span style={{ position: 'absolute', width: 64, height: 64, marginLeft: -32, borderRadius: '50%', border: '1px solid rgba(var(--amber-rgb),0.4)', '--nxa': 'rcRing 2.4s ease-out infinite 1.2s' }} />
          </div>
        )}
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 46, filter: 'drop-shadow(0 6px 22px rgba(var(--amber-rgb),0.4))' }}>{isPublic ? '📖' : '🔖'}</div>
          <h2 style={{ margin: '18px 0 8px', color: 'var(--text-strong)', fontFamily: 'var(--font-serif)', fontSize: 23, fontWeight: 500 }}>
            {isPublic ? 'Published to the Reading Room' : 'Saved to your shelf'}
          </h2>
          <p style={{ margin: '0 auto', maxWidth: 300, color: 'var(--text-dim)', fontSize: 14, lineHeight: 1.55 }}>
            {isPublic
              ? `Your take${showName ? ` on ${showName}` : ''} is live, the community can read it, like it, and reply.`
              : 'Only you can see this one. Flip it to public anytime from the review.'}
          </p>
          <div style={{ marginTop: 26, display: 'flex', gap: 10, width: '100%', maxWidth: 320 }}>
            {isPublic && (
              <button onClick={onView} style={{ flex: 1, padding: 13, borderRadius: 'var(--radius-md)', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-strong)', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                Reading Room
              </button>
            )}
            <button onClick={onClose} className="ui-primary" style={{ flex: 1, padding: 13, borderRadius: 'var(--radius-md)', background: 'var(--grad-amber)', border: 'none', color: 'var(--amber-ink)', fontWeight: 800, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font)' }}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
